/**
 * ============================================================================
 * USER PERSISTENCE PLUGIN
 * ============================================================================
 *
 * Pinia plugin that manages user data persistence to/from localStorage.
 *
 * Responsibilities:
 * 1. Load user data from localStorage on app initialization
 * 2. Run data migrations to keep user data structure up-to-date
 * 3. Execute post-migration hooks (after store is ready)
 * 4. Watch for changes and auto-save to localStorage
 *
 * Flow:
 * 1. App starts → Plugin loads → Read localStorage
 * 2. Run migrations (if needed) → Update data structure
 * 3. Patch store with migrated data
 * 4. Run post-migration hooks (e.g., refresh catalog data)
 * 5. Watch for changes → Auto-save to localStorage
 *
 * Note: Migration logic is in /migrations/ folder for maintainability
 *
 * ============================================================================
 */

import { runMigrations, runPostMigrationHooks } from '@/migrations/index.js';

export const userPersistencePlugin = ({ store }) => {
    // Only apply to mainStore (not other Pinia stores)
    if (store.$id === 'mainStore') {
        // ====================================================================
        // LOAD USER DATA FROM LOCALSTORAGE
        // ====================================================================
        const savedUserState = localStorage.getItem('luxxbox_acoustic_analyzer_user');

        if (savedUserState) {
            try {
                // Parse saved JSON data
                const parsedUserData = JSON.parse(savedUserState);

                // Run migrations to update data structure if needed
                // This ensures user data is always in the latest format
                const migratedUserData = runMigrations(parsedUserData);

                // Load migrated data into the store
                store.$patch({ user: migratedUserData });

                // Run post-migration hooks (e.g., refresh product catalog data)
                // These hooks need store access, so they run after data is loaded
                runPostMigrationHooks(migratedUserData, store);
            } catch (error) {
                // Handle corrupted localStorage data gracefully
                console.error('[Persistence Plugin] Failed to load or migrate user data:', error);
                // Store will use default state instead
            }
        } else {
            console.log('[Persistence Plugin] No saved user data found, using default state');
        }

        // ====================================================================
        // AUTO-SAVE ON CHANGES
        // ====================================================================
        // Watch for any changes to user state and automatically save to localStorage
        // This ensures user progress is never lost

        store.$subscribe((_mutation, state) => {
            try {
                localStorage.setItem('luxxbox_acoustic_analyzer_user', JSON.stringify(state.user));
            } catch (error) {
                // Handle localStorage quota exceeded or other errors
                console.error('[Persistence Plugin] Failed to save user data:', error);
            }
        });
    }
};
